import React from 'react';
import { SlLimitedInput, SlLimitedInputProps } from 'src/components/BeaconV2Components/SlLimitedInput';
import color from 'src/utils/colors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SxProps } from '@mui/system';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { FormControl } from '@mui/material';

interface StyledSettingProps {
  label?: string;
  textSx?: SxProps;
  required?: boolean;
  height?: string;
}

const StyledInput = styled(SlLimitedInput)(({ height = '32px' }: { height?: string }) => ({ width: '100%',
  marginTop: '8px',
  padding: '10px 14px',
  boxSizing: 'border-box',
  height: height,
  // SlLimitedInput ultimately renders a plain HTML <input> (not MUI InputBase),
  // so we must override the native disabled styling here.
  '& input:disabled': {
    WebkitTextFillColor: color.blueGrey, // #BEC7D3
    color: color.blueGrey,
    opacity: 1
  },
  '& .MuiInputBase-root': {
    borderRadius: '4px',
    border: `1px solid ${color.graysSecondary}`,
    backgroundColor: color.white,
    height: '40px',
    fontSize: '14px'
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: color.blueGrey, // #BEC7D3
    color: color.blueGrey,
    opacity: 1
  },
  '& .MuiFormLabel-root': {
    color: color.blueGrey
  }
}));

export const InputLabel: React.FC<StyledSettingProps> = ({ textSx, label, required }) => {
  return (
    <Text
      variant="h7"
      sx={{
        ...textSx,
        display: 'flex',
        lineHeight: '20px'
      }}
    >
      {label}
      {required && (
        <Text
          variant="body3"
          sx={{
            lineHeight: '18px',
            marginLeft: '2px'
          }}
        >
          *
        </Text>
      )}
    </Text>
  );
};

export const StyledSettingInputLabel: React.FC<StyledSettingProps> = ({ label, textSx, required }) => {
  return <InputLabel label={label} textSx={textSx} required={required} />;
};

type RHFInputProps<T extends FieldValues> = StyledSettingProps & {
  name: Path<T>; // name is a required prop
  characterLimit: number;
  placeholder: string;
  defaultValue?: string; // provide empty string, if no default value is needed
  filterRegex?: RegExp; // Optional regex to filter input characters
  disabled?: boolean;
};

export function StyledSettingInput<T extends FieldValues>(allProps: RHFInputProps<T>) {
  const { control } = useFormContext();
  const {
    name,
    characterLimit,
    placeholder,
    label,
    defaultValue = '',
    required = false,
    textSx = undefined,
    filterRegex,
    disabled = false,
    ...otherInputProps
  } = allProps;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as any}
      render={({ field }) => {
        const originalOnChange = field.onChange;

        const handleFilteredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = event.target.value;
          let filteredValue = rawValue;
          if (filterRegex) {
            const disallowedCharsRegex = new RegExp(`[^${filterRegex.source}]`, 'g');
            filteredValue = rawValue.replace(disallowedCharsRegex, '');
          }
          originalOnChange(filteredValue); // Updates RHF state
        };

        return (
          <FormControl>
            {label && <StyledSettingInputLabel textSx={textSx} label={label} required={required} />}
            <StyledInput
              {...field}
              value={field.value} // Controlled by RHF state
              onChange={handleFilteredChange}
              characterLimit={characterLimit}
              placeholder={placeholder}
              disabled={disabled}
              {...otherInputProps}
            />
          </FormControl>
        );
      }}
    />
  );
}
